import React, { useEffect, useState, useRef, useMemo } from 'react';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as ChowDeckIcon } from 'assets/icons/chowdeck-logo.svg';
import { ReactComponent as GlovoIcon } from 'assets/icons/glovo-logo.svg';

import './styles.scss';
import { ENUM_LIVE_CARD_MENU } from './menu-config';
import { useSearchParams } from 'react-router-dom';
import { OrderType } from 'models/order.model';
import {
  calcDeliveryTime,
  getOrderStatus,
  isChowdeckOrder,
  isCSRole,
  isGlovoOrder
} from 'utils/functions';
import OrderStatus from 'components/base/order-status';
import OrderDetail from 'pages/order-details';
import OrderReceiptProvider from 'components/cs/order-receipt';
import ReceiptContent from 'components/cs/order-receipt/receipt-content';
import ReactToPrint from 'react-to-print';
import { BrandsIcon } from 'cokitchen-icons';
import { STORAGE_KEYS } from 'utils/constants';
import { ENUM_CS_ROLE } from '@cokitchen/cokitchen-auth';
import { BaseModal } from '@cokitchen/cokitchen-components';
import CSLiveTripCard from './cs-live-trip-card';
import BaseLiveTripCard from './base-live-trip-card';
import LiveTripModals from './live-trip-modals';

type Props = {
  orders: OrderType[];
  modalView?: boolean;
  ridersAvailable?: boolean;
};

const LiveTripCards = ({ orders, modalView, ridersAvailable = false }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeOrder, setActiveOrder] = useState<OrderType>();
  const [showOrderModal, setShowOrderModal] = useState(false);
  const receiptRef = useRef<HTMLInputElement>(null);
  const role = localStorage.getItem(STORAGE_KEYS.ROLE);
  const [activeMenu, setActiveMenu] = useState<{
    order?: OrderType;
    menuKey?: ENUM_LIVE_CARD_MENU;
  }>({});

  const sortedOrders = useMemo(() => {
    const sorted = orders.sort((a, b) => {
      if (Object.values(ENUM_CS_ROLE).includes(role as ENUM_CS_ROLE)) {
        const aTime = calcDeliveryTime(a?.estimated_delivery_time, a?.kitchen_dispatched_time) ?? 0;
        const bTime = calcDeliveryTime(b?.estimated_delivery_time, b?.kitchen_dispatched_time) ?? 0;
        return aTime - bTime;
      } else {
        return new Date(b?.created_at ?? '').getTime() - new Date(a?.created_at ?? '').getTime();
      }
    });

    return sorted;
  }, [orders]);

  useEffect(() => {
    setShowOrderModal(!!activeOrder);
    if (activeOrder) {
      searchParams.set('order', activeOrder.order_code);
      searchParams.set('orderStatus', getOrderStatus(activeOrder));
    } else {
      if (searchParams.has('order')) {
        searchParams.delete('order');
        searchParams.delete('orderStatus');
      }
    }
    setSearchParams(searchParams);
  }, [activeOrder]);

  const closeOrderModal = () => {
    setActiveOrder(undefined);
  };

  const LiveCardComponent = useMemo(() => {
    if (isCSRole()) return CSLiveTripCard;

    return BaseLiveTripCard;
  }, []);

  return (
    <div
      className={`grid ${!modalView ? 'md:grid-cols-2 lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4`}>
      {sortedOrders.map((order) => (
        <LiveCardComponent
          key={order.id}
          onClick={() => setActiveOrder(order)}
          order={order}
          onSelectMenu={(menuKey) => setActiveMenu({ menuKey, order })}
          ridersAvailable={ridersAvailable}
        />
      ))}

      <BaseModal
        position='right'
        isOpen={showOrderModal}
        onRequestClose={closeOrderModal}
        size='wide'
        title={
          <div className='flex flex-wrap lg:flex-nowrap gap-2 items-center'>
            <button onClick={closeOrderModal}>
              <ArrowLeftIcon className='w-6 h-6' />
            </button>
            <div className='font-semibold text-2xl flex items-center gap-3'>
              {isChowdeckOrder(activeOrder ?? null) ? (
                <ChowDeckIcon />
              ) : isGlovoOrder(activeOrder ?? null) ? (
                <GlovoIcon />
              ) : null}
              <span>Order #{activeOrder?.order_code?.toUpperCase()}</span>
            </div>
            {activeOrder && (
              <div className='basis-full lg:basis-auto grow'>
                <OrderStatus type={getOrderStatus(activeOrder)} />
              </div>
            )}
            {isCSRole() && (
              <div className='absolute right-16'>
                <ReactToPrint
                  trigger={() => (
                    <button className='flex items-center gap-1 border-b font-medium text-sm text-gray-5'>
                      <BrandsIcon />
                      Print docket
                    </button>
                  )}
                  content={() => receiptRef.current}
                />
              </div>
            )}

            <div className='receipt py-4' ref={receiptRef}>
              <OrderReceiptProvider order={activeOrder as OrderType}>
                <ReceiptContent />
              </OrderReceiptProvider>
            </div>
          </div>
        }>
        {activeOrder && <OrderDetail orderCode={activeOrder?.order_code} />}
      </BaseModal>

      <LiveTripModals activeMenu={activeMenu} onClose={() => setActiveMenu({})} />
    </div>
  );
};

export default LiveTripCards;
